import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { loader } from './loader.js'
import { Events } from '../events'

gsap.registerPlugin(ScrollTrigger)

// The loader is an opaque mask. All jank-prone setup (component mounts, ScrollTrigger
// pins + refresh, font/image load) happens *behind* it; only once the app has settled
// do we play the destination entrance together with the mask exit. This controller owns
// that ordering/gating only — it never inspects which Swup transition is running and
// never picks a specific mask visual (the caller hands it a `playExit`).

const MIN_DISPLAY_LOAD = 600
const MIN_DISPLAY_SWUP = 300
const CEILING = 4000

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))

const documentFontsReady = () => (document.fonts?.ready ? document.fonts.ready.catch(() => {}) : Promise.resolve())

/** Small `{ promise, resolve }` deferred, used to surface async mount completion. */
export const createDeferred = () => {
	let resolve
	const promise = new Promise((res) => {
		resolve = res
	})
	return { promise, resolve }
}

/**
 * Core sequencer. Keeps the mask covering, waits for the app to settle behind it
 * (bounded by CEILING), forces the reflow, enforces a minimum display, then runs the
 * destination entrance and the mask exit together. The mask visual is owned by the
 * loader module; `playExit` defaults to `loader.hide` but a transition may inject its
 * own exit for a different mask.
 */
const playReveal = async ({ entry, settle, minDisplay, playExit = () => loader.hide() }) => {
	// Ensure the mask covers the screen while we settle behind it.
	await loader.show({ animate: false })

	const start = performance.now()

	// Mounts + pins built behind the mask (ceiling guards against a hung mount).
	await Promise.race([settle(), wait(CEILING)])

	// Force the layout-shifting reflow now, while it's hidden by the mask.
	ScrollTrigger.refresh()

	await documentFontsReady()

	const elapsed = performance.now() - start
	if (elapsed < minDisplay) await wait(minDisplay - elapsed)

	const eventBus = window.app?.eventBus

	// Destination page's entrance (page-reveal registry, keyed by template + entry).
	window.app?.runPageReveal?.(entry)
	eventBus?.emit(Events.APP_REVEAL_START, { entry })

	await playExit()

	eventBus?.emit(Events.APP_REVEAL_COMPLETE, { entry })
}

/** Initial page load. Settles on all components having mounted. */
export const revealOnLoad = (componentsManager) =>
	playReveal({
		entry: 'direct',
		minDisplay: MIN_DISPLAY_LOAD,
		settle: () => new Promise((resolve) => (componentsManager ? componentsManager.init(resolve) : resolve())),
	})

/**
 * Swup navigation. Settles on the per-visit `componentsReady` deferred resolved by the
 * content:replace hook. `playExit` lets each transition own its own mask-exit visual.
 */
export const revealOnSwup = ({ playExit } = {}) => {
	const isHistory = window.ctx?.currentTransition?.history?.popstate === true
	return playReveal({
		entry: isHistory ? 'history' : 'swup',
		minDisplay: MIN_DISPLAY_SWUP,
		settle: () => window.ctx?.componentsReady?.promise ?? Promise.resolve(),
		playExit,
	})
}
